import React, { useCallback, useMemo, useState } from "react";
import { getPriceErrorMessage } from "../booking";
import { getBookingPrice } from "../booking/api";
import { Link } from "react-router-dom";
import Select from "react-select";
import { tripTypeOptions, vehicleOptions, getFilteredVehicleOptions } from "../vehicles";
import CustomPlacesAutocomplete from "../CustomPlacesAutocomplete";
import styled from "styled-components";
import { OutlineButton } from "../../components/layout/OutlineButton";
import { DateTimeInput } from "../../components/layout/DateTimeInput";
import { useCustomerOptionsByProvider } from "../customer";

export function BookingForm({
  userInfos,
  tarifs,
  data,
  setData,
  index,
  duplicateBooking,
  deleteBooking,
  provider,
  disabled,
  bookingId,
  isEstimate = true,
}) {
  const customerOptions = useCustomerOptionsByProvider(provider?.id);
  const filteredVehicleOptions = useMemo(() => getFilteredVehicleOptions(userInfos), [userInfos]);
  const [dateError, setDateError] = useState("");
  
  // Calculate minimum date based on whether it's an estimate or invoice
  const minDate = useMemo(() => {
    if (isEstimate) {
      // Estimate: today or future
      return new Date().toISOString().split('T')[0];
    } else {
      // Invoice: no lower limit on date
      return "1900-01-01";
    }
  }, [isEstimate]);

  function handleChange(e) {
    const targetName = e.target.name;
    const targetValue = e.target.value;
    
    // Clear date error when date changes
    if (targetName === "pickupDate") {
      setDateError("");
    }
    
    setData((old) => ({ ...old, [targetName]: targetValue }));
  }
  
  // Validate date format
  const validateDate = useCallback((dateString) => {
    if (!dateString) return "";
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      return "Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.";
    }
    
    // Validate that the date is actually valid (not 2025-02-30, etc.)
    const dateParts = dateString.split('-');
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    const dateObj = new Date(year, month - 1, day);
    
    if (
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day ||
      year < 1900 || 
      year > 2100
    ) {
      return "Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.";
    }
    
    // Check date constraints based on type
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateObj.setHours(0, 0, 0, 0);

    if (isEstimate) {
      // Estimate: must be today or future
      if (dateObj < today) {
        return "La date du devis ne peut pas être dans le passé.";
      }
    }
    // Invoice: no date range restriction

    return "";
  }, [isEstimate]);
  
  // Validate date when it changes or loses focus
  const handleDateBlur = useCallback(() => {
    const error = validateDate(data.pickupDate);
    setDateError(error);
  }, [data.pickupDate, validateDate]);

  const calculatePriceDisabledMsg = useMemo(() => {
    return getPriceErrorMessage(
      data.pickupAddress,
      data.dropoffAddress,
      data.tripType?.value,
      data.numHours,
      data.vehicle?.value,
      data.tarif?.value
    );
  }, [data]);

  const [loadingPrice, setLoadingPrice] = useState(false);

  const computePrice = useCallback(async () => {
    setLoadingPrice(true);

    const price = await getBookingPrice(
      userInfos.jwt,
      data.pickupAddress,
      data.dropoffAddress,
      data.tripType.value,
      data.numHours,
      data.vehicle.value,
      data.tarif.value,
      data.tarif_product?.value,
      data.pickupPlaceId,
      data.dropoffPlaceId,
      // Build coords objects here (inside useCallback, not a problem)
      data.pickupLat && data.pickupLon ? { lat: data.pickupLat, lon: data.pickupLon } : null,
      data.dropoffLat && data.dropoffLon ? { lat: data.dropoffLat, lon: data.dropoffLon } : null
    );
    setData((old) => ({ ...old, price: parseFloat(price.client) }));
    setLoadingPrice(false);
  }, [data, setData, userInfos.jwt]);

  return (
    <>
      <div className="d-flex gap-3 mb-4">
        {bookingId ? (
          <h3>
            <Link to={"/booking/" + bookingId}>
              N° de la course: {bookingId}
            </Link>
          </h3>
        ) : (
          <h3>Course n° {index + 1}</h3>
        )}
        <div className="d-flex gap-3">
          {!disabled && (
            <>
              <OutlineButton onClick={duplicateBooking}>
                <i className="fa fa-copy"></i> Dupliquer cette course
              </OutlineButton>
              <OutlineButton onClick={deleteBooking}>
                <i className="fa fa-trash"></i> Supprimer cette course
              </OutlineButton>
            </>
          )}
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Passager *</label>
        <div className="col-lg-6">
          <Select
            value={data.customer ? {
              ...data.customer,
              label: data.passengerFirstname && data.passengerLastname
                ? `${data.passengerFirstname} ${data.passengerLastname}`
                : data.customer.label
            } : null}
            options={customerOptions}
            isDisabled={disabled || !provider?.id}
            onChange={(val) => {
              setData((old) => ({ ...old, customer: val }));
            }}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Nombre de passagers *</label>
        <div className="col-lg-6">
          <input
            type="number"
            className={"form-control"}
            value={data.passengersNumber}
            name={"passengersNumber"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Nombre de bagages *</label>
        <div className="col-lg-6">
          <input
            type="number"
            className={"form-control"}
            value={data.bagsNumber}
            name={"bagsNumber"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Type de trajet *</label>
        <div className="col-lg-6">
          <Select
            value={data.tripType}
            onChange={(val) => setData((old) => ({ ...old, tripType: val }))}
            options={tripTypeOptions}
            isDisabled={disabled}
          />
        </div>
      </div>
      {data.tripType && data.tripType.value === "hourly" ? (
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">
            Durée en heures (trajet à l'heure) *
          </label>
          <div className="col-lg-6">
            <input
              type="number"
              className={"form-control"}
              value={data.numHours}
              name={"numHours"}
              onChange={handleChange}
              disabled={disabled}
            />
          </div>
        </div>
      ) : null}
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Véhicule *</label>
        <div className="col-lg-6">
          <Select
            value={data.vehicle}
            onChange={(val) => setData((old) => ({ ...old, vehicle: val }))}
            options={filteredVehicleOptions}
            isDisabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Tarif *</label>
        <div className="col-lg-6">
          <Select
            value={data.tarif}
            onChange={(val) => setData((old) => ({ ...old, tarif: val }))}
            options={tarifs}
            isDisabled={disabled}
          />
        </div>
      </div>
      {data?.tarif?.data && data?.tarif?.data?.products?.length > 3 && (
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Tarif {'->'} Produit</label>
          <div className="col-lg-6">
            <Select
              value={data.tarif_product}
              onChange={(val) =>
                setData((old) => ({ ...old, tarif_product: val }))
              }
              options={[
                { value: "", label: "Aucun" },
                ...data.tarif.data.products.slice(3).map((product) => ({
                  value: product.id,
                  label: product.label,
                })),
              ]}
              isDisabled={disabled}
            />
          </div>
        </div>
      )}
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Date du départ *</label>
        <div className="col-lg-6">
          <DateTimeInput
            type="date"
            className={dateError ? "form-control is-invalid" : "form-control"}
            value={data.pickupDate}
            name={"pickupDate"}
            min={minDate}
            placeholder="JJ/MM/AAAA"
            onChange={handleChange}
            onBlur={handleDateBlur}
            disabled={disabled}
          />
          {dateError && (
            <div className="invalid-feedback d-block">
              {dateError}
            </div>
          )}
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Heure du départ *</label>
        <div className="col-lg-6">
          <DateTimeInput
            type="time"
            className={"form-control"}
            value={data.pickupTime}
            name={"pickupTime"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Adresse de départ *</label>
        <div className="col-lg-6">
          <CustomPlacesAutocomplete
            setSelection={(val) =>
              setData((old) => ({
                ...old,
                pickupAddress: val.display_name,
                pickupAddressInput: val.display_name,
                pickupPlaceId: val.place_id || null,
                // Always set coordinates (to null if not available)
                pickupLat: val.lat || null,
                pickupLon: val.lon || null
              }))
            }
            value={data.pickupAddressInput}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Adresse d'arrivée *</label>
        <div className="col-lg-6">
          <CustomPlacesAutocomplete
            setSelection={(val) =>
              setData((old) => ({
                ...old,
                dropoffAddress: val.display_name,
                dropoffAddressInput: val.display_name,
                dropoffPlaceId: val.place_id || null,
                // Always set coordinates (to null if not available)
                dropoffLat: val.lat || null,
                dropoffLon: val.lon || null
              }))
            }
            value={data.dropoffAddressInput}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Numéro de vol/train</label>
        <div className="col-lg-6">
          <input
            type="text"
            className={"form-control"}
            value={data.flightTrainNumber}
            name={"flightTrainNumber"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">
          Informations supplémentaires
        </label>
        <div className="col-lg-6">
          <textarea
            className={"form-control"}
            value={data.additionalInfo}
            name={"additionalInfo"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Prix</label>
        <div className="col-lg-6">
          <input
            type="number"
            className={"form-control"}
            value={data.price}
            name={"price"}
            onChange={handleChange}
            disabled={disabled}
          />
          {!disabled && (
            <CalculatePriceBtn
              disabled={!!calculatePriceDisabledMsg || loadingPrice}
              onClick={computePrice}
            >
              {loadingPrice
                ? "Calcul en cours..."
                : "Calculer le prix" +
                  (calculatePriceDisabledMsg
                    ? ` (${calculatePriceDisabledMsg})`
                    : "")}
            </CalculatePriceBtn>
          )}
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">TVA</label>
        <div className="col-lg-6">
          <input
            type="number"
            className={"form-control"}
            value={data.tva}
            name={"tva"}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
}

export const CalculatePriceBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 0.9em;
  text-decoration: underline;
  transition: all ease 300ms;
  color: #444;

  &:hover {
    color: black;
  }

  &:disabled {
    cursor: default;
    color: #777;
  }
`;
